import {
  AlertColor,
  DialogContent,
  Drawer,
  FormHelperText,
} from "@mui/material";
import { Formik, useFormikContext } from "formik";
import ErrorRibbon from "../../../common/ErrorRibbon";
import WriteableSolutionFormContainer from "../../form/components";
import ProgrammeInfoContainer from "../../form/components/programme-info";

import {
  mappedMethodologyField,
  interventionTypeOptions,
  assetTypeOptions,
} from "../../utils";
import { DetailedSolutionApiResponse } from "../../types";
import { EditSolutionFormValues } from "../form/types";
import { createEditSolutionFormValues } from "../form/defaultValues";
import { editSolutionFormSchema } from "../form/schema";
import SingleSelect from "../../../common/SingleSelect";

export type SolutionEditAlert = {
  type: AlertColor;
  message: string;
  errorCode?: number;
} | null;

export type UpdateSolutionDetailsDrawerProps = {
  isEditing: boolean;
  handleEdit: (value: boolean) => void;
  updateSolution: (formData: EditSolutionFormValues) => void;
  solutionDetails: DetailedSolutionApiResponse;
  loading: boolean;
  errorAlert: {
    details: SolutionEditAlert;
    setAlert: (alert: SolutionEditAlert) => void;
  };
};

export type EditSolutionFormFieldsProps = {
  submitLoading: boolean;
  handleCancel: () => void;
};

function EditSolutionFormFields({
  submitLoading,
  handleCancel,
}: EditSolutionFormFieldsProps) {
  const { isValid, values } = useFormikContext<EditSolutionFormValues>();
  const websiteGuidanceMessage = "This value appears on the marketing website";

  return (
    <WriteableSolutionFormContainer
      submitLoading={submitLoading}
      submitDisabled={!isValid}
      handleCancel={handleCancel}
      title="Edit Project"
      assessmentMethodologyField={
        <>
          <SingleSelect
            options={mappedMethodologyField}
            name="assessment-methodology"
            label="Assessment Methodology"
            fieldPath="solution-assessment-methodology"
            warnInsteadOfError={true}
            value={values["solution-assessment-methodology"]}
          />
          {!values["solution-assessment-methodology"] && (
            <FormHelperText sx={{ color: "warning.main" }}>
              {websiteGuidanceMessage}
            </FormHelperText>
          )}
        </>
      }
      interventionTypeField={
        <>
          <SingleSelect
            name="intervention-type"
            label="Intervention Type"
            options={interventionTypeOptions}
            fieldPath="solution-intervention-type"
            warnInsteadOfError={true}
            value={values["solution-intervention-type"]}
          />
          {!values["solution-intervention-type"] && (
            <FormHelperText sx={{ color: "warning.main" }}>
              {websiteGuidanceMessage}
            </FormHelperText>
          )}
        </>
      }
      programmeInfoSection={
        <ProgrammeInfoContainer
          assetTypeField={
            <>
              <SingleSelect
                name="asset-type"
                label="Asset Type (Credit Type)"
                options={assetTypeOptions}
                fieldPath="programme-info.asset-type"
                warnInsteadOfError={true}
                value={values["programme-info"]["asset-type"]}
              />
              {!values["programme-info"]["asset-type"] && (
                <FormHelperText sx={{ color: "warning.main" }}>
                  {websiteGuidanceMessage}
                </FormHelperText>
              )}
            </>
          }
        />
      }
    />
  );
}

export function UpdateSolutionDetailsDrawer({
  loading,
  isEditing,
  handleEdit,
  updateSolution,
  solutionDetails,
  errorAlert,
}: UpdateSolutionDetailsDrawerProps) {
  return (
    <Drawer
      data-testid="update-solution-drawer"
      anchor="bottom"
      open={isEditing}
    >
      <DialogContent>
        <ErrorRibbon data={errorAlert} />
        <Formik<EditSolutionFormValues>
          initialValues={createEditSolutionFormValues(solutionDetails)}
          validationSchema={editSolutionFormSchema}
          onSubmit={updateSolution}
          validateOnMount
          initialTouched={{
            "solution-name": true,
            "solution-vbc-website-display-name": true,
            "solution-developer": true,
            "solution-assessment-methodology": true,
            "solution-intervention-type": true,
            "solution-short-description": true,
            "solution-development-stage": true,
            "solution-is-ready-for-sale": true,
            "solution-start-date": true,
            "solution-end-date": true,
            latitude: true,
            longitude: true,
            interventions: [
              {
                action: true,
                ecosystem: true,
              },
            ],
            "internal-details": {
              "website-url": true,
            },
            "programme-info": {
              "asset-type": true,
              "carbon-crediting-programme": true,
            },
            "risk-monitoring": {
              "is-risk-alert-enabled": false,
              "buffer-value-km": false,
              "geospatial-file": false,
              "geospatial-id": false,
            },
          }}
        >
          <EditSolutionFormFields
            submitLoading={loading}
            handleCancel={() => {
              errorAlert.setAlert(null);
              handleEdit(false);
            }}
          />
        </Formik>
      </DialogContent>
    </Drawer>
  );
}
